import type { ComponentType } from 'react';

import getNewDesignItems from 'menu-items/newDesign';
import { isMenuItemVisible, isSectionVisible, type MenuAccess } from 'menu-items/menuVisibility';
import { isAdminVisible } from 'pages/admin/adminItems';
import { FeatureFlag } from 'types/features';
import { SHARE_WITH_FIRM_PERMISSIONS } from 'types/homeWidgetSharing';
import PinDragDemo from './PinDragDemo';
import { TOUR_MATTER_ID, type TourSample } from './tourMode';
import WidgetArrangeDemo from './WidgetArrangeDemo';

export type TourStep = {
    /** Names the step in its tour's copy (`<wording>.<id>.title` / `.body`) and in the analytics events. */
    id: string;
    /** The page the step shows, opened from the sample data. */
    path: string;
    /** The `data-tour` value of the element the step points at. */
    target: string;
    /**
     * What the page has to be showing for the target to be there, such as an open dialog. The page reads it from
     * `useTour()` and opens it for as long as it is the running step's, saving nothing. The layout is inert during
     * a tour but a dialog is portalled out of it: whatever a state opens is rendered `inert` itself.
     */
    state?: string;
    /** Whether the real user could use what the step points at. */
    when?: (user: MenuAccess) => boolean;
    /** Drawn under the card's body. */
    demo?: ComponentType;
    /** Where the card stands against the target while there is room: to its right, unless the step says under it. */
    side?: 'bottom';
};

const CASE_SCOPES = [
    'MY_CASES',
    'MY_TEAM',
    'MY_TEAM.NON_CONFIDENTIAL',
    'CONFIDENTIAL',
    'NON_CONFIDENTIAL',
    'NON_CONFIDENTIAL_MY_CONTACTS',
    'ADMIN_CASES',
];
// Leads have no team-only-non-confidential or admin form.
const LEAD_SCOPES = ['MY_CASES', 'MY_TEAM', 'CONFIDENTIAL', 'NON_CONFIDENTIAL', 'NON_CONFIDENTIAL_MY_CONTACTS'];

// The case-level codes only: a bare prefix would also match field-level ones such as UPDATE.MATTERS.DMS_LINK.
const CASE_READ_CODES = CASE_SCOPES.map((scope) => `READ.MATTERS.${scope}`);
const CASE_EDIT_CODES = [
    ...CASE_SCOPES.map((scope) => `UPDATE.MATTERS.${scope}`),
    ...LEAD_SCOPES.map((scope) => `UPDATE.MATTERS.LEADS.${scope}`),
];

const holdsAny = (user: MenuAccess, codes: string[]) => !!user?.permissions?.some(({ code }) => codes.includes(code));

export const canReadCases = (user: MenuAccess) => holdsAny(user, CASE_READ_CODES);
export const canEditCases = (user: MenuAccess) => holdsAny(user, CASE_EDIT_CODES);

const menuEntries = () => getNewDesignItems().flatMap((group) => group.children ?? []);

const isMenuLinkVisible = (id: string, user: MenuAccess) => {
    const link = menuEntries()
        .flatMap((entry) => [entry, ...(entry.children ?? [])])
        .find((item) => item.id === id);
    return !!link && isMenuItemVisible(link, user);
};

const caseListSteps = (user: MenuAccess) => isMenuLinkVisible('cases', user) && canReadCases(user);

const CASE_PATH = `/matter/${TOUR_MATTER_ID}/general`;

/** The new design's tour, in order. A step whose `when` fails, or whose target is not on its page, is left out. */
export const TOUR_STEPS: TourStep[] = [
    { id: 'search', path: '/home', target: 'sidebar-search' },
    { id: 'my-cases', path: '/home', target: 'home-my-cases', when: canReadCases },
    { id: 'my-time', path: '/home', target: 'home-my-time', when: (user) => isMenuLinkVisible('time', user) },
    { id: 'actions', path: '/home', target: 'topbar-actions' },
    { id: 'inbox', path: '/home', target: 'sidebar-inbox' },
    {
        id: 'sections',
        path: '/home',
        target: 'sidebar-sections',
        when: (user) => menuEntries().some((entry) => entry.type === 'collapse' && isSectionVisible(entry, user)),
    },
    { id: 'views', path: '/matters', target: 'list-views', when: caseListSteps },
    { id: 'filters', path: '/matters', target: 'list-add-filter', when: caseListSteps },
    { id: 'edit-in-place', path: CASE_PATH, target: 'record-section-edit', when: canEditCases },
    { id: 'pin', path: CASE_PATH, target: 'record-pin', when: canReadCases },
    { id: 'pinned', path: '/home', target: 'sidebar-pinned', demo: PinDragDemo },
    { id: 'admin', path: '/home', target: 'sidebar-admin', when: isAdminVisible },
    { id: 'new-design', path: '/home', target: 'sidebar-new-design' },
];

// What the creator's third tab asks for: the tenant's flag and the permission.
const hasAssistant = (user: MenuAccess) =>
    !!user?.enabledFeatures?.includes(FeatureFlag.AI_CHAT_ENABLED) && holdsAny(user, ['READ.CRESPECT_AI']);

/**
 * The tour of the Home widgets, in order, all on Home. `arranging` is Home being arranged, `add-gallery` the Add a
 * widget dialog on its Firm gallery tab and `create` the creator on its first tab. The creator step is worded
 * twice, for whoever has the assistant's tab in it and for whoever has not.
 */
export const WIDGETS_TOUR_STEPS: TourStep[] = [
    { id: 'widgets', path: '/home', target: 'home-widget' },
    { id: 'figure', path: '/home', target: 'home-widget-figure' },
    { id: 'locked', path: '/home', target: 'home-widget-lock' },
    { id: 'customise', path: '/home', target: 'home-customise' },
    { id: 'arrange', path: '/home', target: 'home-widget-controls', state: 'arranging', demo: WidgetArrangeDemo },
    { id: 'add', path: '/home', target: 'home-add-widget', state: 'arranging' },
    { id: 'gallery', path: '/home', target: 'add-widget-gallery', state: 'add-gallery' },
    // Under the tabs, so the card leaves the creator's preview in sight.
    {
        id: 'create',
        path: '/home',
        target: 'widget-creator-ways',
        state: 'create',
        side: 'bottom',
        when: hasAssistant,
    },
    {
        id: 'create-no-assistant',
        path: '/home',
        target: 'widget-creator-ways',
        state: 'create',
        side: 'bottom',
        when: (user) => !hasAssistant(user),
    },
    {
        id: 'share',
        path: '/home',
        target: 'home-widget-menu',
        when: (user) => holdsAny(user, SHARE_WITH_FIRM_PERMISSIONS),
    },
    { id: 'done', path: '/home', target: 'home-tour' },
];

/** The steps of a tour the user could follow, before the ones whose target does not render are dropped. */
export const stepsFor = (user: MenuAccess, steps: TourStep[] = TOUR_STEPS): TourStep[] =>
    steps.filter((step) => !step.when || step.when(user));

export type Tour = {
    /** In order. A step whose `when` fails, or whose target is not on its page, is left out. */
    steps: TourStep[];
    /** Builds the sample data the tour runs on. Imported as the tour starts, so it stays out of the main bundle. */
    sample: (user: MenuAccess) => Promise<TourSample>;
    /** Where the steps' copy is: `<wording>.<step id>.title` and `.body`. */
    wording: string;
};

/** Every tour, under the id that starts it and that names it in the analytics events. */
export const TOURS: Record<string, Tour> = {
    design: {
        steps: TOUR_STEPS,
        sample: async (user) => (await import('./fixtures')).buildTourSample({ canEditCases: canEditCases(user) }),
        wording: 'tour.step',
    },
    widgets: {
        steps: WIDGETS_TOUR_STEPS,
        sample: async (user) =>
            (await import('./fixtures/widgets')).buildWidgetsTourSample({ canEditCases: canEditCases(user) }),
        wording: 'tour.widgets.step',
    },
};
